import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { AuthUser, UserProfile, UpdateProfileRequest } from '../types/auth';
import authAPI from '../services/authApi';
import { authToken, setUnauthorizedHandler, installAuthFetchInterceptor } from '../services/authToken';

interface AuthContextType {
  user: AuthUser | null;
  profile: UserProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isSecretary: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (request: UpdateProfileRequest) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Install the fetch interceptor once at module load so token attachment and
// 401 handling are active before any component mounts.
installAuthFetchInterceptor();

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(() => authToken.get());
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(authToken.get()));

  const clearSession = useCallback(() => {
    authToken.clear();
    setToken(null);
    setUser(null);
    setProfile(null);
  }, []);

  // Route any 401 (surfaced by the fetch interceptor) back to a clean, logged-out
  // state; the route guard then redirects to /login.
  useEffect(() => {
    setUnauthorizedHandler(() => clearSession());
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  // On mount, if a token is present, hydrate identity + profile in one round-trip.
  useEffect(() => {
    const existing = authToken.get();
    if (!existing) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await authAPI.me();
        if (cancelled) return;
        setUser(me.user);
        setProfile(me.profile);
      } catch {
        if (!cancelled) clearSession();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authAPI.login({ email, password });
    authToken.set(result.token);
    setToken(result.token);
    setUser(result.user);
    // Fetch the persona profile after login (may be null for new users).
    try {
      const { profile: p } = await authAPI.getProfile();
      setProfile(p);
    } catch {
      setProfile(null);
    }
  }, []);

  const logout = useCallback(async () => {
    await authAPI.logout();
    clearSession();
  }, [clearSession]);

  const refreshProfile = useCallback(async () => {
    const { profile: p } = await authAPI.getProfile();
    setProfile(p);
  }, []);

  const updateProfile = useCallback(async (request: UpdateProfileRequest) => {
    const { profile: p } = await authAPI.updateProfile(request);
    setProfile(p);
  }, []);

  const value: AuthContextType = {
    user,
    profile,
    token,
    isAuthenticated: Boolean(token && user),
    isSecretary: user?.role === 'secretary',
    isLoading,
    login,
    logout,
    refreshProfile,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
